import { useState } from "react";
import { Pencil } from "lucide-react";
import { Button } from "./button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./dialog";
import { Input } from "./input";
import { Label } from "./label";
import { useToast } from "@/hooks/use-toast";
import { getDisplayName, setDisplayName } from "@/lib/documentMetadata";
import { useInvalidateDocumentDisplayNames } from "@/hooks/useDocumentDisplayNames";

interface FileRenameButtonProps {
  currentUrl: string;
  onRenameSuccess: (newUrl: string) => void;
  disabled?: boolean;
  className?: string;
  iconClassName?: string;
}

export function FileRenameButton({
  currentUrl,
  onRenameSuccess,
  disabled = false,
  className = "h-8 w-8",
  iconClassName = "h-4 w-4",
}: FileRenameButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [newFileName, setNewFileName] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);
  const { toast } = useToast();
  const { invalidateAll } = useInvalidateDocumentDisplayNames();

  const handleOpen = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(true);
    
    // Load current display name from metadata or extract from URL
    const currentDisplayName = await getDisplayName(currentUrl);
    
    // Remove file extension for editing
    const lastDotIndex = currentDisplayName.lastIndexOf('.');
    const nameWithoutExt = lastDotIndex !== -1 
      ? currentDisplayName.substring(0, lastDotIndex) 
      : currentDisplayName;
    
    setNewFileName(nameWithoutExt);
  };

  const handleRename = async () => {
    if (!newFileName.trim()) {
      toast({
        title: "Fehler",
        description: "Bitte geben Sie einen Dateinamen ein",
        variant: "destructive",
      });
      return;
    }

    setIsRenaming(true);

    // Extract current file extension from URL
    const urlParts = currentUrl.split('/');
    const currentFileName = urlParts[urlParts.length - 1].split('?')[0];
    const lastDotIndex = currentFileName.lastIndexOf('.');
    const extension = lastDotIndex !== -1 ? currentFileName.substring(lastDotIndex) : '';

    // Add extension to new filename for display
    const fullNewFileName = newFileName.trim() + extension;

    // Use metadata-based renaming (instant, no file operations)
    const result = await setDisplayName(currentUrl, fullNewFileName);

    setIsRenaming(false);

    if (result.success) {
      // Invalidate all document display name caches
      invalidateAll();
      
      toast({
        title: "Erfolg",
        description: "Dateiname erfolgreich geändert",
      });
      // Pass the same URL back - the display name is now in the database
      onRenameSuccess(currentUrl);
      setIsOpen(false);
    } else {
      toast({
        title: "Fehler",
        description: result.error || "Fehler beim Ändern des Dateinamens",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={handleOpen}
        disabled={disabled}
        className={className}
        data-stop-upload
      >
        <Pencil className={`${iconClassName} text-muted-foreground`} />
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent data-stop-upload onClick={(e) => e.stopPropagation()} onPointerDownCapture={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Datei umbenennen</DialogTitle>
            <DialogDescription>
              Geben Sie einen neuen Dateinamen ein.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="newFileName">Neuer Dateiname</Label>
              <Input
                id="newFileName"
                value={newFileName}
                onChange={(e) => setNewFileName(e.target.value)}
                placeholder="Dateiname eingeben"
                disabled={isRenaming}
              />
              <p className="text-sm text-muted-foreground">
                Die Dateiendung wird automatisch beibehalten
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
              disabled={isRenaming}
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleRename(); }}
              disabled={isRenaming}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {isRenaming ? "Wird umbenannt..." : "Umbenennen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
